import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { InlineEdit } from '@/components/ui/inline-edit.tsx';
import { Click2CallTrigger } from '@/components/ui/click-2-call-trigger';
import { SendMailTrigger } from '@/components/ui/send-mail-trigger';
import { Customer } from '@/models/customer.ts';
import { useResetCustomerPassword, useUpdateCustomer } from '@/api/customers/use-customers.tsx';
import { useSnackbar } from 'notistack';
import { getErrorMessage } from '@/utils/errorHandling';
import { NotesWidget } from '@/components/notes/NotesWidget';
import { useUsers } from '@/api/users/use-users';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog.tsx';
import {
    Activity,
    Calendar,
    Clock,
    Copy,
    Eye,
    EyeOff,
    Globe,
    Key,
    Mail,
    MapPin as Location,
    Phone,
    RefreshCw,
    Tag,
    Target,
    User,
    UserCheck,
} from 'lucide-react';
import { useCustomAttributeDefinitionsManagement } from '@/hooks/useCustomAttributes';
import DynamicInput from '@/components/ui/DynamicInput';
import { customAttributesToRequestShape } from '@/utils/customAttributes';
import CustomerAddressesWidget from './CustomerAddressesWidget';
import { ModulePermission, PermissionAction } from '@/types/permissions';
import { generatePassword } from '@/utils/passwordGenerator';
import { useActiveLeadStatuses } from '@/api/lead-statuses/use-lead-statuses';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';
import { formatDate as formatDateUtil } from '@/utils/formatters';
import { usePermissions } from '@/hooks/usePermissions';

interface InfoTabProps {
    customer: Customer;
    onSettingsEdit: () => void;
    refetchCustomer?: () => void;
}

const InfoTab: React.FC<InfoTabProps> = ({ customer, refetchCustomer }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { hasPermission } = usePermissions();
    const { data: systemConfig } = useSystemConfiguration();
    const crmTimezone = systemConfig?.timezone ?? 'UTC';

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' });
            })
            .catch(() => {
                enqueueSnackbar(`Failed to copy ${label}`, { variant: 'error' });
            });
    };

    const [phonePrimaryEditing, setPhonePrimaryEditing] = useState(false);
    const [phoneAdditionalEditing, setPhoneAdditionalEditing] = useState(false);

    const updateCustomerMutation = useUpdateCustomer(customer.id);
    const resetPasswordMutation = useResetCustomerPassword();

    // Fetch active users for assignee dropdown
    const { data: usersResponse } = useUsers({ status: 'active', per_page: 100 });
    const users = usersResponse?.data || [];

    // Fetch custom attribute definitions
    const { definitions: customAttributeDefinitions, isLoading: isLoadingCustomAttributes } =
        useCustomAttributeDefinitionsManagement();

    // Get active lead statuses for dynamic status editing
    const { data: leadStatusesData } = useActiveLeadStatuses();
    const leadStatuses = leadStatusesData?.data || [];
    const rawStatus = customer.status as unknown;
    const selectedLeadStatusValue = (() => {
        if (customer.status?.id) {
            return customer.status.id;
        }

        if (typeof rawStatus === 'string' && rawStatus) {
            const matchedStatus = leadStatuses.find(
                status =>
                    status.id === rawStatus ||
                    status.name === rawStatus ||
                    status.label === rawStatus
            );
            return matchedStatus?.id ?? rawStatus;
        }

        return '';
    })();
    // The customer's current status may belong to a status that is no longer
    // active (e.g. archived), so it can be missing from `leadStatuses`. When
    // that happens, InlineEdit's display falls back to the raw id (a UUID)
    // because it can't find a matching option label. Make sure the currently
    // assigned status always has a matching option so the label renders.
    const statusSelectOptions = useMemo(() => {
        const options = leadStatuses.map(status => ({
            value: status.id,
            label: status.label,
        }));
        if (
            customer.status?.id &&
            customer.status?.label &&
            !options.some(option => option.value === customer.status?.id)
        ) {
            options.push({ value: customer.status.id, label: customer.status.label });
        }
        return options;
    }, [leadStatuses, customer.status]);
    const isActiveToggleOn = customer.is_active ?? true;
    const canEditCustomer = hasPermission(ModulePermission.CUSTOMERS, PermissionAction.EDIT);

    const handleActiveToggle = async (enabled: boolean) => {
        await handleFieldUpdate('is_active', enabled);
    };

    // Password reset state
    const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleFieldUpdate = async (field: keyof Customer | 'lead_status', value: any) => {
        try {
            await updateCustomerMutation.mutateAsync({ [field]: value });
            enqueueSnackbar('Customer updated successfully', { variant: 'success' });
            // Explicitly refetch customer data to ensure UI updates
            refetchCustomer?.();
        } catch (error: any) {
            enqueueSnackbar(getErrorMessage(error, 'Failed to update customer'), {
                variant: 'error',
            });
        }
    };

    const handleCustomAttributeUpdate = async (attributeName: string, value: any) => {
        try {
            const current = customAttributesToRequestShape(customer.custom_attributes ?? {});
            const customAttributes = { ...current, [attributeName]: value };
            await updateCustomerMutation.mutateAsync({ custom_attributes: customAttributes });
            enqueueSnackbar('Custom attribute updated successfully', { variant: 'success' });
            // Explicitly refetch customer data to ensure UI updates
            refetchCustomer?.();
        } catch (error: any) {
            enqueueSnackbar(getErrorMessage(error, 'Failed to update custom attribute'), {
                variant: 'error',
            });
        }
    };

    const handlePasswordReset = async () => {
        if (newPassword.length < 8) {
            enqueueSnackbar('Password must be at least 8 characters long', { variant: 'error' });
            return;
        }

        try {
            await resetPasswordMutation.mutateAsync({
                customerId: customer.id,
                password: newPassword,
            });
            setIsPasswordDialogOpen(false);
            setNewPassword('');
            setShowPassword(false);
        } catch (error) {
            // Error is handled by the mutation
        }
    };

    const handleGeneratePassword = () => {
        const generated = generatePassword(16);
        setNewPassword(generated);
    };

    const formatDate = (dateString: string) => formatDateUtil(dateString, 'en-GB', crmTimezone);

    return (
        <div className="space-y-6">
            {/* Customer Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Information */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Customer Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Customer Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Name
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <InlineEdit
                                            value={customer.name}
                                            onSave={value => handleFieldUpdate('name', value)}
                                            permissionModule={ModulePermission.CUSTOMERS}
                                            className="text-lg font-semibold"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0"
                                            onClick={() => copyToClipboard(customer.name, 'Name')}
                                            aria-label="Copy name to clipboard"
                                            title="Copy name to clipboard"
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Email
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <InlineEdit
                                            value={customer.email}
                                            onSave={value => handleFieldUpdate('email', value)}
                                            permissionModule={ModulePermission.CUSTOMERS}
                                            className="flex-1 min-w-0"
                                        />
                                        <SendMailTrigger
                                            email={customer.email}
                                            recipientId={customer.id}
                                            recipientType="customer"
                                            recipientName={customer.name}
                                            className="flex-shrink-0"
                                        />
                                        {customer.email && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-8 w-8 p-0 flex-shrink-0"
                                                onClick={() =>
                                                    copyToClipboard(customer.email, 'Email')
                                                }
                                                aria-label="Copy email to clipboard"
                                                title="Copy email to clipboard"
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Phone
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <InlineEdit
                                            value={customer.phone}
                                            onSave={value => handleFieldUpdate('phone', value)}
                                            permissionModule={ModulePermission.CUSTOMERS}
                                            placeholder="No phone number"
                                            type="phone"
                                            className="flex-1 min-w-0"
                                            onEditingChange={setPhonePrimaryEditing}
                                        />
                                        {!phonePrimaryEditing && (
                                            <>
                                                <Click2CallTrigger
                                                    value={customer.phone || ''}
                                                    customerId={customer.id}
                                                    displayMode="buttonOnly"
                                                    className="flex-shrink-0"
                                                />
                                                {customer.phone && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 flex-shrink-0"
                                                        onClick={() =>
                                                            copyToClipboard(
                                                                customer.phone || '',
                                                                'Phone'
                                                            )
                                                        }
                                                        aria-label="Copy phone to clipboard"
                                                        title="Copy phone to clipboard"
                                                    >
                                                        <Copy className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Additional Phone Number
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                        <InlineEdit
                                            value={customer.additional_phone_number}
                                            onSave={value =>
                                                handleFieldUpdate('additional_phone_number', value)
                                            }
                                            permissionModule={ModulePermission.CUSTOMERS}
                                            placeholder="No additional phone number"
                                            type="phone"
                                            className="flex-1 min-w-0"
                                            onEditingChange={setPhoneAdditionalEditing}
                                        />
                                        {!phoneAdditionalEditing && (
                                            <>
                                                <Click2CallTrigger
                                                    value={customer.additional_phone_number || ''}
                                                    customerId={customer.id}
                                                    displayMode="buttonOnly"
                                                    className="flex-shrink-0"
                                                />
                                                {customer.additional_phone_number && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-8 w-8 p-0 flex-shrink-0"
                                                        onClick={() =>
                                                            copyToClipboard(
                                                                customer.additional_phone_number ||
                                                                    '',
                                                                'Additional Phone'
                                                            )
                                                        }
                                                        aria-label="Copy additional phone to clipboard"
                                                        title="Copy additional phone to clipboard"
                                                    >
                                                        <Copy className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Country
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Location className="h-4 w-4 text-muted-foreground" />
                                        <InlineEdit
                                            value={customer.country || ''}
                                            onSave={value => handleFieldUpdate('country', value)}
                                            permissionModule={ModulePermission.CUSTOMERS}
                                            placeholder="No country"
                                            type="country"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Assigned To
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <User className="h-4 w-4 text-muted-foreground" />
                                        <InlineEdit
                                            value={customer.assigned_user?.name || ''}
                                            onSave={value => {
                                                if (!value || value === '__unassigned__') {
                                                    handleFieldUpdate('assigned_user_id', null);
                                                } else {
                                                    const selectedUser = users.find(
                                                        user => user.name === value
                                                    );
                                                    handleFieldUpdate(
                                                        'assigned_user_id',
                                                        selectedUser?.id || null
                                                    );
                                                }
                                            }}
                                            permissionModule={ModulePermission.CUSTOMERS}
                                            placeholder="No assignee"
                                            type="select"
                                            selectOptions={[
                                                { value: '__unassigned__', label: 'Unassigned' },
                                                ...users.map(user => ({
                                                    value: user.name,
                                                    label: `${user.name}${user.title ? ` (${user.title})` : ''}`,
                                                })),
                                            ]}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Status
                                    </label>
                                    <div className="mt-1">
                                        <InlineEdit
                                            value={selectedLeadStatusValue}
                                            onSave={value =>
                                                handleFieldUpdate('lead_status', value)
                                            }
                                            permissionModule={ModulePermission.CUSTOMERS}
                                            type="select"
                                            selectOptions={statusSelectOptions}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Active
                                    </label>
                                    <div className="mt-2 flex items-center gap-3">
                                        <Switch
                                            checked={isActiveToggleOn}
                                            onCheckedChange={handleActiveToggle}
                                            disabled={
                                                !canEditCustomer || updateCustomerMutation.isPending
                                            }
                                        />
                                        <span className="text-sm">
                                            {isActiveToggleOn ? 'Active' : 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Preferred Language
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Globe className="h-4 w-4 text-muted-foreground" />
                                        <InlineEdit
                                            value={
                                                customer.preferred_language ||
                                                customer.settings?.preferred_language ||
                                                'en'
                                            }
                                            onSave={value =>
                                                handleFieldUpdate('preferred_language', value)
                                            }
                                            permissionModule={ModulePermission.CUSTOMERS}
                                            placeholder="No language set"
                                            type="select"
                                            selectOptions={[
                                                { value: 'en', label: 'English' },
                                                { value: 'ar', label: 'العربية (Arabic)' },
                                                { value: 'es', label: 'Español (Spanish)' },
                                                { value: 'fr', label: 'Français (French)' },
                                            ]}
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Contact & Activity */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Clock className="h-5 w-5" />
                                Contact & Activity
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Last Login
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <span>
                                            {customer.last_login_at
                                                ? formatDate(customer.last_login_at)
                                                : 'Never'}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Last Login IP
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Globe className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-mono text-sm">
                                            {customer.last_login_ip || '-'}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Created
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <span>{formatDate(customer.created_at)}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Last Updated
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <span>{formatDate(customer.updated_at)}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Conversion Date
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Target className="h-4 w-4 text-muted-foreground" />
                                        <span>
                                            {customer.converted_at
                                                ? formatDate(customer.converted_at)
                                                : 'Not converted from lead'}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Contact Attempts
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Activity className="h-4 w-4 text-muted-foreground" />
                                        <span className="font-medium">
                                            {customer.contact_attempts || 0}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Last Contact Date
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                        <span>
                                            {customer.last_contact_date
                                                ? formatDate(customer.last_contact_date)
                                                : 'Never'}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Next Follow-up Date
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-muted-foreground" />
                                        <span>
                                            {customer.next_follow_up_date
                                                ? formatDate(customer.next_follow_up_date)
                                                : 'No follow-up scheduled'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Additional Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Tag className="h-5 w-5" />
                                Additional Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Custom Attributes */}
                            {customAttributeDefinitions &&
                                customAttributeDefinitions.length > 0 && (
                                    <>
                                        {isLoadingCustomAttributes ? (
                                            <div className="text-center py-4">
                                                <p className="text-muted-foreground">
                                                    Loading custom attributes...
                                                </p>
                                            </div>
                                        ) : (
                                            customAttributeDefinitions.map(definition => {
                                                const customValue =
                                                    customer.custom_attributes?.[
                                                        definition.name as any
                                                    ]?.value;
                                                return (
                                                    <div key={definition.id}>
                                                        <label className="text-sm font-medium text-muted-foreground">
                                                            {definition.label}
                                                            {definition.is_required && (
                                                                <span className="text-destructive ms-1">
                                                                    *
                                                                </span>
                                                            )}
                                                        </label>
                                                        <div className="mt-1">
                                                            <DynamicInput
                                                                definition={definition}
                                                                value={customValue}
                                                                onChange={value =>
                                                                    handleCustomAttributeUpdate(
                                                                        definition.name,
                                                                        value
                                                                    )
                                                                }
                                                                mode="inline"
                                                                placeholder={`Enter ${definition.label.toLowerCase()}`}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </>
                                )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Lead Conversion Information */}
            {customer.converted_lead_id && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <UserCheck className="h-5 w-5" />
                            Lead Conversion Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Original Lead ID
                                </label>
                                <p className="font-mono text-sm bg-muted p-2 rounded">
                                    {customer.converted_lead_id}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Converted At
                                </label>
                                <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <span>
                                        {customer.converted_at
                                            ? formatDate(customer.converted_at)
                                            : '-'}
                                    </span>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Marketing Campaign
                                </label>
                                <InlineEdit
                                    value={customer.marketing_campaign_id}
                                    onSave={value =>
                                        handleFieldUpdate('marketing_campaign_id', value)
                                    }
                                    permissionModule={ModulePermission.CUSTOMERS}
                                    placeholder="No campaign"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Sales Team
                                </label>
                                <InlineEdit
                                    value={customer.sales_team}
                                    onSave={value => handleFieldUpdate('sales_team', value)}
                                    permissionModule={ModulePermission.CUSTOMERS}
                                    placeholder="No sales team"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    IB Referral ID
                                </label>
                                <InlineEdit
                                    value={customer.ib_referral_id}
                                    onSave={value => handleFieldUpdate('ib_referral_id', value)}
                                    permissionModule={ModulePermission.CUSTOMERS}
                                    placeholder="No IB referral"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Time Zone
                                </label>
                                <InlineEdit
                                    value={customer.time_zone}
                                    onSave={value => handleFieldUpdate('time_zone', value)}
                                    permissionModule={ModulePermission.CUSTOMERS}
                                    placeholder="No time zone"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Regulatory Region
                                </label>
                                <InlineEdit
                                    value={customer.regulatory_region}
                                    onSave={value => handleFieldUpdate('regulatory_region', value)}
                                    permissionModule={ModulePermission.CUSTOMERS}
                                    placeholder="No regulatory region"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Assigned User ID
                                </label>
                                <InlineEdit
                                    value={customer.assigned_user_id}
                                    onSave={value => handleFieldUpdate('assigned_user_id', value)}
                                    permissionModule={ModulePermission.CUSTOMERS}
                                    placeholder="No assigned user"
                                />
                            </div>
                        </div>

                        {/* Instruments of Interest */}
                        {customer.instruments_of_interest &&
                            customer.instruments_of_interest.length > 0 && (
                                <div>
                                    <label className="text-sm font-medium text-muted-foreground">
                                        Instruments of Interest
                                    </label>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {customer.instruments_of_interest.map(
                                            (instrument, index) => (
                                                <span
                                                    key={index}
                                                    className="px-2 py-1 bg-info/10 text-info rounded-md text-sm"
                                                >
                                                    {instrument}
                                                </span>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}

                        {/* Notes */}
                        {customer.notes && (
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Notes
                                </label>
                                <InlineEdit
                                    value={
                                        Array.isArray(customer.notes)
                                            ? customer.notes.join('\n')
                                            : customer.notes || ''
                                    }
                                    onSave={value => handleFieldUpdate('notes', value)}
                                    permissionModule={ModulePermission.CUSTOMERS}
                                    type="textarea"
                                    multiline={true}
                                    placeholder="No notes"
                                    className="mt-1"
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Addresses Widget */}
            <CustomerAddressesWidget
                customerId={customer.id}
                addresses={customer.addresses || []}
            />

            {/* Password Reset Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Key className="h-5 w-5" />
                        Password Management
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="w-full">
                                <Key className="h-4 w-4" />
                                Reset Customer Password
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[425px]">
                            <DialogHeader>
                                <DialogTitle>Reset Customer Password</DialogTitle>
                                <DialogDescription>
                                    Set a new password for {customer.name} ({customer.email}). The
                                    customer will need to use this password to log in.
                                </DialogDescription>
                            </DialogHeader>
                            <div className="grid gap-4 py-4">
                                <div className="grid gap-2">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="new-password">New Password</Label>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleGeneratePassword}
                                            className="h-8 text-xs"
                                        >
                                            <RefreshCw className="h-3 w-3" />
                                            Generate
                                        </Button>
                                    </div>
                                    <div className="relative">
                                        <Input
                                            id="new-password"
                                            type={showPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            placeholder="Enter new password"
                                            className="pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setIsPasswordDialogOpen(false);
                                        setNewPassword('');
                                        setShowPassword(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handlePasswordReset}
                                    disabled={resetPasswordMutation.isPending}
                                >
                                    {resetPasswordMutation.isPending
                                        ? 'Resetting...'
                                        : 'Reset Password'}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </CardContent>
            </Card>

            {/* Notes & Tasks Widget */}
            <NotesWidget entityType="customers" entityId={customer.id} entityName={customer.name} />
        </div>
    );
};

export default InfoTab;
